import { defaultPropertyDefinitions as propertyDefinitions } from "@buildwithflux/constants";
import { getPartType } from "@buildwithflux/models";
import { values } from "lodash";
import { getManufacturerProperty } from "../../property/PropertyAPI";
import { getPropertyValueIfString } from "../../property/PropertyAPI";
import { getNormalizedManufacturerName } from "../manufacturer/manufacturerAliases";
import { partTypeClassifier } from "./partAttributeClassifiers";
// Normalizers that will fix spelling issues and such things but not try to make fuzzy guesses
export function getNormalizedMount(partVersionData) {
    const mountPropValue = getPropertyValueIfString(propertyDefinitions.mount_type.label, partVersionData.properties);
    // fallback to auto detect mount from part type
    const rawPartType = getPartType(partVersionData);
    const partMetadataStringLower = (mountPropValue || rawPartType + " " + partVersionData.name + " " + partVersionData.description).toLowerCase();
    if (partMetadataStringLower?.includes("surface mount") ||
        partMetadataStringLower?.includes("surface-mount") ||
        partMetadataStringLower?.includes("smd") ||
        partMetadataStringLower?.includes("smt")) {
        return "Surface Mount";
    }
    else if (partMetadataStringLower?.includes("through hole") ||
        partMetadataStringLower?.includes("through-hole") ||
        partMetadataStringLower?.includes("th") ||
        partMetadataStringLower?.includes("tht") ||
        partMetadataStringLower?.includes("thm")) {
        return "Through Hole";
    }
    else if (partMetadataStringLower?.includes("stud")) {
        return "Stud";
    }
    else if (partMetadataStringLower?.includes("chassis")) {
        return "Chassis";
    }
    else if (partMetadataStringLower?.includes("screw")) {
        return "Screw";
    }
    else if (partMetadataStringLower?.includes("axial")) {
        return "Axial";
    }
    else if (partMetadataStringLower?.includes("radial")) {
        return "Radial";
    }
    else if (partMetadataStringLower?.includes("panel")) {
        return "Panel";
    }
    else if (partMetadataStringLower?.includes("reverse")) {
        return "Reverse";
    }
    else if (partMetadataStringLower?.includes("standard")) {
        return "Standard";
    }
    else if (partMetadataStringLower?.includes("clamp")) {
        return "Clamp";
    }
    else if (partMetadataStringLower?.includes("din rail")) {
        return "DIN Rail";
    }
    else if (partMetadataStringLower?.includes("solder")) {
        return "Solder";
    }
    else if (partMetadataStringLower?.includes("pcb")) {
        return "PCB";
    }
    else if (partMetadataStringLower?.includes("cable")) {
        return "Cable";
    }
    else if (partMetadataStringLower?.includes("box")) {
        return "Box";
    }
    else if (partMetadataStringLower?.includes("base")) {
        return "Base";
    }
    else if (partMetadataStringLower?.includes("wire")) {
        return "Wire";
    }
    else if (partMetadataStringLower?.includes("plugable")) {
        return "Plugable";
    }
    else if (partMetadataStringLower?.includes("press fit")) {
        return "Press Fit";
    }
    else if (partMetadataStringLower?.includes("hardware")) {
        return "Hardware";
    }
    else if (partMetadataStringLower?.includes("flanges")) {
        return "Flanges";
    }
    else if (partMetadataStringLower?.includes("plug-in")) {
        return "Plug-in";
    }
    else if (partMetadataStringLower?.includes("socket")) {
        return "Socket";
    }
    else if (partMetadataStringLower?.includes("vertical")) {
        return "Vertical";
    }
    if (mountPropValue?.trim()) {
        return "Other";
    }
    return undefined;
}
export function getNormalizedPartType(partVersionData) {
    const rawPartType = getPartType(partVersionData);
    // Used the part type filter UI to find dupes
    // Generally agreed that for this field in algolia we want pluralized strings
    // This powers the part type filter menu, so its user facing!
    const lowerCasePartType = rawPartType?.toLowerCase().trim();
    if (!lowerCasePartType) {
        return undefined;
    }
    // via https://octopart.com/electronic-parts
    if (lowerCasePartType.includes("pre-ordered") ||
        lowerCasePartType.includes("pre ordered") ||
        lowerCasePartType.includes("new-arrival") ||
        lowerCasePartType.includes("new arrival") ||
        lowerCasePartType.includes("adi") ||
        lowerCasePartType.includes("padauk") ||
        lowerCasePartType.includes("NUVOTON".toLowerCase()) ||
        lowerCasePartType.includes("CYPRESS".toLowerCase()) ||
        lowerCasePartType.includes("SONIX".toLowerCase()) ||
        lowerCasePartType.includes("MEGAWIN".toLowerCase()) ||
        lowerCasePartType.includes("HOLTEK".toLowerCase()) ||
        lowerCasePartType.includes("piano") ||
        lowerCasePartType.includes("IMU".toLowerCase()) ||
        lowerCasePartType.includes("Geehy".toLowerCase()) ||
        lowerCasePartType.includes("HK".toLowerCase()) ||
        lowerCasePartType.includes("sinowealth") ||
        lowerCasePartType.includes("mindmotion") ||
        lowerCasePartType.includes("hitrendtech") ||
        lowerCasePartType.includes("MDT".toLowerCase()) ||
        lowerCasePartType.includes("TO127P2020X500X2100-15".toLowerCase())) {
        return "Other";
    }
    else if (lowerCasePartType.includes("resistor") || lowerCasePartType.includes("thermistor")) {
        return "Resistors";
    }
    else if (lowerCasePartType.includes("transformer")) {
        return "Transformers";
    }
    else if (lowerCasePartType.includes("capacitor") || lowerCasePartType.includes("mlcc")) {
        return "Capacitors";
    }
    else if (lowerCasePartType.includes("connector") ||
        lowerCasePartType.includes("header") ||
        lowerCasePartType.includes("terminal") ||
        lowerCasePartType.includes("usb") ||
        lowerCasePartType.includes("hdmi") ||
        lowerCasePartType.includes("socket")) {
        return "Connectors";
    }
    else if (lowerCasePartType.includes("oscillator") ||
        lowerCasePartType.includes("crystal") ||
        lowerCasePartType.includes("resonator")) {
        return "Crystals and Oscillators";
    }
    else if (lowerCasePartType.includes("sensor") ||
        lowerCasePartType.includes("photodiode") ||
        lowerCasePartType.includes("rotary encoder") ||
        lowerCasePartType.includes("phototransistor")) {
        return "Sensors";
    }
    else if (lowerCasePartType.includes("diode")) {
        return "Diodes";
    }
    else if (lowerCasePartType.includes("rectifier")) {
        return "Rectifiers";
    }
    else if (lowerCasePartType.includes("transistor") ||
        lowerCasePartType.includes("mosfet") ||
        lowerCasePartType.includes("igbt")) {
        return "Transistors";
    }
    else if (lowerCasePartType.includes("thyristor") || lowerCasePartType.includes("power module")) {
        return "Thyristors";
    }
    else if (lowerCasePartType.includes("esd") ||
        lowerCasePartType.includes("tvs") ||
        lowerCasePartType.includes("varistor") ||
        lowerCasePartType.includes("fuse") ||
        lowerCasePartType.includes("esd") ||
        lowerCasePartType.includes("protection") ||
        lowerCasePartType.includes("glass discharge tube") ||
        lowerCasePartType.includes("breaker")) {
        return "Circuit Protection Devices";
    }
    else if (lowerCasePartType.includes("inductor")) {
        return "Inductors";
    }
    else if (lowerCasePartType.includes("batteries") ||
        lowerCasePartType.includes("battery") ||
        lowerCasePartType.includes("power supply") ||
        lowerCasePartType.includes("surge") ||
        lowerCasePartType.includes("ups") ||
        lowerCasePartType.includes("uninterruptible power supply") ||
        lowerCasePartType.includes("inverter")) {
        return "Power Supply Products";
    }
    else if (lowerCasePartType.includes("led") ||
        lowerCasePartType.includes("light emitting diode") ||
        lowerCasePartType.includes("fiber") ||
        lowerCasePartType.includes("optic") ||
        lowerCasePartType.includes("opto") ||
        lowerCasePartType.includes("lamp") ||
        lowerCasePartType.includes("laser") ||
        lowerCasePartType.includes("display")) {
        return "Optoelectronic Devices";
    }
    else if (lowerCasePartType.includes("optocoupler")) {
        return "Optocouplers";
    }
    else if (lowerCasePartType.includes("relay") ||
        lowerCasePartType.includes("switch") ||
        lowerCasePartType.includes("motor") ||
        lowerCasePartType.includes("drive") ||
        lowerCasePartType.includes("audio") ||
        lowerCasePartType.includes("buzzer") ||
        lowerCasePartType.includes("thermal")) {
        return "Electromechanical Components";
    }
    else if (lowerCasePartType.includes("development board") || lowerCasePartType.includes("dev board")) {
        return "Development Boards";
    }
    else if (lowerCasePartType.includes("evaluation board") || lowerCasePartType.includes("eval board")) {
        return "Evaluation Boards";
    }
    else if (lowerCasePartType.includes("integrated circuit") ||
        lowerCasePartType.includes("ic") ||
        lowerCasePartType.includes("clock buffer") ||
        lowerCasePartType.includes("clock driver") ||
        lowerCasePartType.includes("clock generator") ||
        lowerCasePartType.includes("pll") ||
        lowerCasePartType.includes("frequency synthesizer") ||
        lowerCasePartType.includes("controlled oscillator") ||
        lowerCasePartType.includes("rtc") ||
        lowerCasePartType.includes("real time clock") ||
        lowerCasePartType.includes("timer") ||
        lowerCasePartType.includes("oscillator") ||
        lowerCasePartType.includes("data converter") ||
        lowerCasePartType.includes("analog to digital converter") ||
        lowerCasePartType.includes("adc") ||
        lowerCasePartType.includes("digital potentiometer") ||
        lowerCasePartType.includes("digital to analog converter") ||
        lowerCasePartType.includes("dac") ||
        lowerCasePartType.includes("touch screen controller") ||
        lowerCasePartType.includes("cpld") ||
        lowerCasePartType.includes("dsp") ||
        lowerCasePartType.includes("fpga") ||
        lowerCasePartType.includes("microcontroller") ||
        lowerCasePartType.includes("microprocessor") ||
        lowerCasePartType.includes("spld") ||
        lowerCasePartType.includes("single board computer") ||
        lowerCasePartType.includes("sbc") ||
        lowerCasePartType.includes("system on module") ||
        lowerCasePartType.includes("som") ||
        lowerCasePartType.includes("can transceiver") ||
        lowerCasePartType.includes("codec") ||
        lowerCasePartType.includes("ethernet interface") ||
        lowerCasePartType.includes("rs-232") ||
        lowerCasePartType.includes("rs-422") ||
        lowerCasePartType.includes("rs-485") ||
        lowerCasePartType.includes("uart interface") ||
        lowerCasePartType.includes("usb interface") ||
        lowerCasePartType.includes("lna") ||
        lowerCasePartType.includes("low noise amplifier") ||
        lowerCasePartType.includes("amplifier") ||
        lowerCasePartType.includes("audio amplifier") ||
        lowerCasePartType.includes("operational amplifier") ||
        lowerCasePartType.includes("opamp") ||
        lowerCasePartType.includes("op amp") ||
        lowerCasePartType.includes("op-amp") ||
        lowerCasePartType.includes("buffer amplifier") ||
        lowerCasePartType.includes("instrumentation amplifier") ||
        lowerCasePartType.includes("analog switches") ||
        lowerCasePartType.includes("analog multiplexer") ||
        lowerCasePartType.includes("comparator") ||
        lowerCasePartType.includes("logic gate") ||
        lowerCasePartType.includes("multiplexer") ||
        lowerCasePartType.includes("buffer") ||
        lowerCasePartType.includes("driver") ||
        lowerCasePartType.includes("counter") ||
        lowerCasePartType.includes("decoder") ||
        lowerCasePartType.includes("latch") ||
        lowerCasePartType.includes("flip flop") ||
        lowerCasePartType.includes("shift register") ||
        lowerCasePartType.includes("voltage level shifter") ||
        lowerCasePartType.includes("eeprom") ||
        lowerCasePartType.includes("fifo") ||
        lowerCasePartType.includes("flash") ||
        lowerCasePartType.includes("ram") ||
        lowerCasePartType.includes("ac-dc") ||
        lowerCasePartType.includes("acdc") ||
        lowerCasePartType.includes("ac-dc converter") ||
        lowerCasePartType.includes("acdc converter") ||
        lowerCasePartType.includes("ac-dc controller") ||
        lowerCasePartType.includes("acdc controller") ||
        lowerCasePartType.includes("battery management") ||
        lowerCasePartType.includes("dc-dc") ||
        lowerCasePartType.includes("dcdc") ||
        lowerCasePartType.includes("dc-dc switching controller") ||
        lowerCasePartType.includes("dcdc switching controller") ||
        lowerCasePartType.includes("dc-dc converter") ||
        lowerCasePartType.includes("dcdc converter") ||
        lowerCasePartType.includes("dcdc-converter") ||
        lowerCasePartType.includes("gate driver") ||
        lowerCasePartType.includes("hot swap controller") ||
        lowerCasePartType.includes("ldo voltage regulator") ||
        lowerCasePartType.includes("led driver") ||
        lowerCasePartType.includes("motor driver") ||
        lowerCasePartType.includes("power factor correction controller") ||
        lowerCasePartType.includes("power management") ||
        lowerCasePartType.includes("power switch ic") ||
        lowerCasePartType.includes("voltage reference") ||
        lowerCasePartType.includes("linear voltage regulator") ||
        lowerCasePartType.includes("switching voltage regulator") ||
        lowerCasePartType.includes("voltage supervisor") ||
        lowerCasePartType.includes("rf amplifier") ||
        lowerCasePartType.includes("rf antenna") ||
        lowerCasePartType.includes("rf attenuator") ||
        lowerCasePartType.includes("rf mixer") ||
        lowerCasePartType.includes("rf transceiver") ||
        lowerCasePartType.includes("rf receiver") ||
        lowerCasePartType.includes("rf detector") ||
        lowerCasePartType.includes("rf module") ||
        lowerCasePartType.includes("rf filter") ||
        lowerCasePartType.includes("rf chip") ||
        lowerCasePartType.includes("ntc thermistor") ||
        lowerCasePartType.includes("mcu") ||
        lowerCasePartType.includes("dropout regulator") ||
        lowerCasePartType.includes("memory") ||
        lowerCasePartType.includes("emmc") ||
        lowerCasePartType.includes("soc") ||
        lowerCasePartType.includes("2g/3g/4g/5g module") ||
        lowerCasePartType.includes("5g module") ||
        lowerCasePartType.includes("4g module") ||
        lowerCasePartType.includes("3g module") ||
        lowerCasePartType.includes("2g module") ||
        lowerCasePartType.includes("wifi module") ||
        lowerCasePartType.includes("wireless module") ||
        lowerCasePartType.includes("bluetooth module") ||
        lowerCasePartType.includes("lora module") ||
        lowerCasePartType.includes("antenna") ||
        lowerCasePartType.includes("io expander") ||
        lowerCasePartType.includes("i/o expander") ||
        lowerCasePartType.includes("monitors & reset circuit") ||
        lowerCasePartType.includes("reset circuit") ||
        lowerCasePartType.includes("monitor") ||
        lowerCasePartType.includes("gate") ||
        lowerCasePartType.includes("register") ||
        lowerCasePartType.includes("isolator") ||
        lowerCasePartType.includes("interface") ||
        lowerCasePartType.includes("74 series") ||
        lowerCasePartType.includes("multivibrator") ||
        lowerCasePartType.includes("power source") ||
        lowerCasePartType.includes("stc") ||
        lowerCasePartType.includes("lin transceiver") ||
        lowerCasePartType.includes("infrared remote receiver") ||
        lowerCasePartType.includes("infrared receiver") ||
        lowerCasePartType.includes("diplexer") ||
        lowerCasePartType.includes("atmel") ||
        lowerCasePartType.includes("balun") ||
        lowerCasePartType.includes("infineon") ||
        lowerCasePartType.includes("renesas") ||
        lowerCasePartType.includes("zigbee") ||
        lowerCasePartType.includes("uart") ||
        lowerCasePartType.includes("level shifter")) {
        return "Integrated Circuits";
    }
    else if (lowerCasePartType.includes("common mode filter") ||
        lowerCasePartType.includes("filter") ||
        lowerCasePartType.includes("emi") ||
        lowerCasePartType.includes("rfi") ||
        lowerCasePartType.includes("saw filter") ||
        lowerCasePartType.includes("ferrite bead")) {
        return "EMI / RFI Components";
    }
    else if (lowerCasePartType.includes("module")) {
        return "Modules";
    }
    else {
        return partTypeClassifier(partVersionData);
    }
}
export function getNormalizedManufacturerForPart(partVersionData) {
    const rawManufacturer = getManufacturerProperty(partVersionData.properties)?.value;
    const manufacturerAsString = typeof rawManufacturer === "string" ? rawManufacturer : undefined;
    if (!manufacturerAsString) {
        return undefined;
    }
    return getNormalizedManufacturerName(manufacturerAsString) || "Other";
}
function findEiaStandardSizePackage(componentType, input) {
    // Ensure the component type is in uppercase to match the array format
    let typePrefix;
    switch (componentType) {
        case "Resistors":
            typePrefix = "R";
            break;
        case "Capacitors":
            typePrefix = "C";
            break;
        case "Inductors":
            typePrefix = "L";
            break;
        case "Diodes":
            typePrefix = "D";
            break;
        default:
            return undefined;
    }
    const eiaStandardSizes = {
        "01005": `${typePrefix}01005`,
        "015008": `${typePrefix}015008`,
        "0201": `${typePrefix}0201`,
        "0402": `${typePrefix}0402`,
        "0503": `${typePrefix}0503`,
        "0504": `${typePrefix}0504`,
        "0603": `${typePrefix}0603`,
        "0805": `${typePrefix}0805`,
        "1005": `${typePrefix}1005`,
        "1206": `${typePrefix}1206`,
        "1210": `${typePrefix}1210`,
        "1218": `${typePrefix}1218`,
        "1812": `${typePrefix}1812`,
        "2010": `${typePrefix}2010`,
        "2220": `${typePrefix}2220`,
        "2512": `${typePrefix}2512`,
    };
    for (const [size, code] of Object.entries(eiaStandardSizes)) {
        if (input.includes(size)) {
            return code;
        }
    }
    return undefined;
}
export function getNormalizedPackageCaseCode(partVersionData) {
    const rawPackageCaseCode = getPropertyValueIfString(propertyDefinitions.package_case_code.label, partVersionData.properties);
    const partMetadataStringLower = (rawPackageCaseCode +
        " " +
        partVersionData.name +
        " " +
        partVersionData.description).toLowerCase();
    const normalizedInput = partMetadataStringLower.replace(/[-_\s]/g, " ");
    const packageOptions = propertyDefinitions.package_case_code.auto_complete_options || [];
    // Collect all matches and prefer the best one based on match quality
    let bestMatch;
    let bestMatchScore = 0; // Higher score = better match
    for (const type of packageOptions) {
        const normalizedType = type.replace(/[-_\s]/g, " ").toLowerCase();
        // Exact match gets highest priority
        if (normalizedInput === normalizedType) {
            return type;
        }
        const typeParts = normalizedType.split(" ");
        const inputParts = normalizedInput.split(" ");
        // Check if every part of the package type is present in the input, regardless of order
        if (typeParts.every((part) => inputParts.includes(part))) {
            // Score based on length - longer matches are more specific and better
            // For ties (same length), prefer letter-first format (e.g., "SOT-23" over "23-SOT")
            // as this is the canonical naming convention
            const lengthScore = type.length * 1000;
            const letterFirstBonus = /^[A-Z]/i.test(type) ? 1 : 0;
            const score = lengthScore + letterFirstBonus;
            if (score > bestMatchScore) {
                bestMatch = type;
                bestMatchScore = score;
            }
        }
        else if (normalizedInput.startsWith(normalizedType)) {
            // startsWith match - use length as score
            const lengthScore = type.length * 1000;
            const letterFirstBonus = /^[A-Z]/i.test(type) ? 1 : 0;
            const score = lengthScore + letterFirstBonus;
            if (score > bestMatchScore) {
                bestMatch = type;
                bestMatchScore = score;
            }
        }
    }
    if (bestMatch)
        return bestMatch;
    const partType = getNormalizedPartType(partVersionData);
    const eiaCode = findEiaStandardSizePackage(partType || "Resistors", partMetadataStringLower);
    if (eiaCode) {
        return eiaCode;
    }
    if (!rawPackageCaseCode?.trim()) {
        return undefined;
    }
    return "Other";
}
export function getTerminalCount(partVersionData) {
    return values(partVersionData.terminals).length;
}
export function getNormalizedCompliance(partVersionData) {
    const rawComplianceValue = getPropertyValueIfString(propertyDefinitions.compliance.label, partVersionData.properties);
    const lowerCaseMetaData = (rawComplianceValue +
        " " +
        partVersionData.name +
        " " +
        partVersionData.description).toLowerCase();
    const compliance = [];
    if (lowerCaseMetaData.includes("rohs") || lowerCaseMetaData.includes("skyworks green")) {
        compliance.push("Rohs");
    }
    if (lowerCaseMetaData.includes("reach") || lowerCaseMetaData.includes("skyworks green")) {
        compliance.push("REACH");
    }
    if (lowerCaseMetaData.includes("dfars")) {
        compliance.push("DFARS");
    }
    if (lowerCaseMetaData.includes("aec q100") || lowerCaseMetaData.includes("aec-q100")) {
        compliance.push("AEC-Q100");
    }
    if (lowerCaseMetaData.includes("aec q200") || lowerCaseMetaData.includes("aec-q200")) {
        compliance.push("AEC-Q200");
    }
    if (lowerCaseMetaData.includes("conflict minerals")) {
        compliance.push("Conflict Minerals (USA)");
    }
    if (lowerCaseMetaData.includes("nipdau")) {
        compliance.push("NiPdAu");
    }
    if (lowerCaseMetaData.includes("green")) {
        compliance.push("Green");
    }
    if (lowerCaseMetaData.includes("pb-free") || lowerCaseMetaData.includes("pb free")) {
        compliance.push("Pb-Free");
    }
    if (lowerCaseMetaData.includes("pfos") || lowerCaseMetaData.includes("skyworks green")) {
        compliance.push("PFOS");
    }
    if (lowerCaseMetaData.includes("iec5 62474") ||
        lowerCaseMetaData.includes("iec5-62474") ||
        lowerCaseMetaData.includes("skyworks green")) {
        compliance.push("IEC5-62474");
    }
    if (lowerCaseMetaData.includes("halogen") || lowerCaseMetaData.includes("skyworks green")) {
        compliance.push("Halogen Free");
    }
    if (lowerCaseMetaData.includes("antimony")) {
        compliance.push("Antimony Free");
    }
    if (lowerCaseMetaData.includes("lead free") || lowerCaseMetaData.includes("lead-free")) {
        compliance.push("Lead Free");
    }
    if (lowerCaseMetaData.includes("weee")) {
        compliance.push("WEEE");
    }
    if (lowerCaseMetaData.includes("ul 1577") || lowerCaseMetaData.includes("ul-1577")) {
        compliance.push("UL-1577");
    }
    if (lowerCaseMetaData.includes("vde din en60747-5-5") || lowerCaseMetaData.includes("vde 0884-5")) {
        compliance.push("VDE DIN EN60747-5-5 (VDE 0884-5)");
    }
    if (lowerCaseMetaData.includes("csa ca5a") || lowerCaseMetaData.includes("csa-ca5a")) {
        compliance.push("CSA CA5A");
    }
    if (lowerCaseMetaData.includes("gb4943.1-2011") || lowerCaseMetaData.includes("gb8898-2011")) {
        compliance.push("CQC GB4943.1-2011/GB8898-2011");
    }
    if (lowerCaseMetaData.includes("nordic safety") ||
        lowerCaseMetaData.includes("fimko") ||
        lowerCaseMetaData.includes("nemko") ||
        lowerCaseMetaData.includes("semko") ||
        lowerCaseMetaData.includes("demko")) {
        compliance.push("Nordic Safety (FIMKO/NEMKO/SEMKO/DEMKO)");
    }
    if (lowerCaseMetaData.includes("bsi")) {
        compliance.push("BSI");
    }
    if (lowerCaseMetaData.includes("fcc")) {
        compliance.push("FCC");
    }
    if (lowerCaseMetaData.includes("msl3")) {
        compliance.push("MSL3");
    }
    if (compliance.length === 0) {
        if (rawComplianceValue?.trim()) {
            compliance.push("Other");
            return compliance;
        }
        return undefined;
    }
    return compliance;
}
//# sourceMappingURL=partAttributeNormalizers.js.map